import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { eur, dateIt, dateTimeIt } from "@/lib/format";
import { OPERATION_STATUSES, OPERATION_SKIPPABLE, pipelineFor } from "@/lib/constants";
import { PageHeader, OperationStatusBadge, Field } from "@/components/shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ArrowLeft, Building2, User, History, Save, CheckCircle2, CalendarClock, FileDown, Mail, Send, Check, Workflow } from "lucide-react";

const CONCLUDED = "Incasso completato";
const COST_KEYS = ["taxes_notary_fees", "renovation_costs", "technical_costs", "financial_costs", "holding_costs", "marketing_costs", "contingency_costs"];
const num = (v) => { const n = parseFloat(v); return isNaN(n) ? 0 : n; };

function computeEcon(f) {
  const pp = num(f.purchase_price);
  const total_cost = pp + COST_KEYS.reduce((a, k) => a + num(f[k]), 0);
  const selling = num(f.selling_costs);
  const invested = num(f.invested_capital);
  const esp = num(f.expected_sale_price);
  const asp = num(f.actual_sale_price);
  const received = num(f.amount_received);
  const expected_profit = esp > 0 ? esp - total_cost - selling : null;
  const actual_profit = asp > 0 ? asp - total_cost - selling : null;
  const margin_pct = (asp > 0 && actual_profit !== null) ? (actual_profit / asp) * 100 : null;
  const roi_pct = (invested > 0 && actual_profit !== null) ? (actual_profit / invested) * 100 : null;
  const base_sale = asp > 0 ? asp : esp;
  const amount_pending = base_sale > 0 ? base_sale - received : null;
  return { pp, total_cost, expected_profit, actual_profit, margin_pct, roi_pct, amount_pending };
}

const TODO = "Da completare";
const money = (v) => (v === null || v === undefined ? TODO : eur(v));
const pctFmt = (v) => (v === null || v === undefined ? TODO : `${v.toFixed(1)}%`);

function PhaseStepper({ status, phases }) {
  const list = phases || OPERATION_STATUSES;
  const cur = list.indexOf(status);
  return (
    <div className="flex flex-wrap gap-2" data-testid="operation-phase-stepper">
      {list.map((s, i) => {
        const done = cur > i;
        const current = cur === i;
        const base = "flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs transition-colors";
        const cls = done
          ? "bg-primary/15 border-primary/30 text-primary font-medium"
          : current
            ? "bg-primary text-white border-primary font-semibold shadow-sm ring-2 ring-primary/30"
            : "bg-slate-50 border-slate-200 text-slate-400";
        return (
          <div key={s} className={`${base} ${cls}`} data-testid={`phase-step-${i}${current ? "-current" : done ? "-done" : ""}`}>
            <span className={`grid place-items-center h-4 w-4 rounded-full shrink-0 ${done ? "bg-primary text-white" : current ? "bg-white/25 text-white" : "bg-slate-200 text-slate-500"}`}>
              {done ? <Check className="h-3 w-3" /> : <span className="text-[9px] font-bold">{i + 1}</span>}
            </span>
            {s}
          </div>
        );
      })}
    </div>
  );
}

function MoneyInput({ label, value, onChange, testid }) {
  return (
    <Field label={label}>
      <Input type="number" step="0.01" value={value} onChange={(e) => onChange(e.target.value)} data-testid={testid} />
    </Field>
  );
}

export default function OperationDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [op, setOp] = useState(null);
  const [newStatus, setNewStatus] = useState("");
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState(null);
  const [sendOpen, setSendOpen] = useState(false);
  const [sendEmail, setSendEmail] = useState("");
  const [sending, setSending] = useState(false);

  const load = useCallback(() => {
    api.get(`/operations/${id}`).then((r) => {
      const d = r.data;
      setOp(d); setNewStatus(d.status);
      setForm({
        next_action: d.next_action || "", notes: d.notes || "",
        start_date: d.start_date || "", preliminary_date: d.preliminary_date || "", deed_date: d.deed_date || "",
        expected_sale_date: d.expected_sale_date || "", actual_sale_date: d.actual_sale_date || d.sale_date || "",
        cash_in_date: d.cash_in_date || "",
        purchase_price: d.purchase_price ?? 0,
        taxes_notary_fees: d.taxes_notary_fees ?? 0, renovation_costs: d.renovation_costs ?? 0,
        technical_costs: d.technical_costs ?? 0, financial_costs: d.financial_costs ?? 0,
        holding_costs: d.holding_costs ?? 0, marketing_costs: d.marketing_costs ?? 0,
        contingency_costs: d.contingency_costs ?? 0, selling_costs: d.selling_costs ?? 0,
        invested_capital: d.invested_capital ?? 0, amount_received: d.amount_received ?? 0,
        expected_sale_price: d.expected_sale_price ?? 0, actual_sale_price: d.actual_sale_price ?? 0,
        cessionario: d.cessionario || "", cession_contact: d.cession_contact || "", cession_notes: d.cession_notes || "",
        documents: d.documents || [],
      });
    }).catch(() => toast.error("Operazione non trovata"));
  }, [id]);
  useEffect(() => { load(); }, [load]);

  const derived = useMemo(() => (form ? computeEcon(form) : null), [form]);

  const updateStatus = async () => {
    try { await api.post(`/operations/${id}/status`, { status: newStatus }); toast.success("Stato aggiornato"); load(); }
    catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const saveDetails = async () => {
    setSaving(true);
    try {
      await api.put(`/operations/${id}`, { ...form, sale_date: form.actual_sale_date || "", status: op.status });
      toast.success("Operazione aggiornata"); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const toggleDoc = (name) => setForm((f) => ({ ...f, documents: f.documents.map((d) => d.name === name ? { ...d, done: !d.done } : d) }));

  const downloadPdf = async () => {
    try {
      const r = await api.get(`/operations/${id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `scheda-operazione-${(op.property_label || "operazione").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Impossibile generare il PDF"); }
  };

  const openSend = () => { setSendEmail(op.client_email || op.contact_email || ""); setSendOpen(true); };

  const doSend = async () => {
    setSending(true);
    try {
      const r = await api.post(`/operations/${id}/send-pdf`, { email: sendEmail });
      toast.success(`Scheda PDF inviata a ${r.data.sent_to}`);
      setSendOpen(false); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setSending(false); }
  };

  const completeReminder = async (r) => {
    try {
      await api.put(`/followups/${r.id}`, {
        linked_type: r.linked_type, linked_id: r.linked_id, linked_name: r.linked_name,
        date: r.date, type: r.type, outcome: r.outcome, responsible: r.responsible,
        next_action: r.next_action, next_action_date: r.next_action_date, status: "Chiuso", notes: r.notes,
      });
      toast.success("Promemoria completato"); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  if (!op || !form) return <div className="p-8 text-muted-foreground">Caricamento...</div>;

  return (
    <div data-testid="operation-detail">
      <button className="flex items-center gap-1 text-sm text-muted-foreground hover:text-primary mb-4" onClick={() => navigate("/operazioni-in-corso")} data-testid="back-operations">
        <ArrowLeft className="h-4 w-4" />Torna alle operazioni
      </button>
      <PageHeader title={op.property_label || "Operazione"} subtitle={`Scheda operazione · ${op.type || "Tipologia non impostata"}`}>
        {op.status === CONCLUDED && (
          <>
            <Button variant="outline" onClick={downloadPdf} data-testid="download-operation-pdf"><FileDown className="h-4 w-4 mr-2" />Scarica PDF</Button>
            <Button onClick={openSend} data-testid="send-operation-pdf"><Mail className="h-4 w-4 mr-2" />Invia scheda PDF</Button>
          </>
        )}
      </PageHeader>

      {/* Top summary — visible without scrolling */}
      <Card className="p-6 mb-6" data-testid="operation-summary">
        <div className="flex flex-col lg:flex-row lg:items-start lg:justify-between gap-4">
          <div className="space-y-1.5">
            <div className="flex items-center gap-2 text-slate-900">
              <Building2 className="h-5 w-5 text-primary shrink-0" />
              <span className="text-lg font-semibold" data-testid="operation-address">{op.property_label || "—"}</span>
            </div>
            <div className="flex flex-wrap items-center gap-x-6 gap-y-1 text-sm text-muted-foreground pl-7">
              <span>Tipo: <span className="font-medium text-slate-700" data-testid="operation-type-badge">{op.type || "—"}</span></span>
              <span>Responsabile: <span className="font-medium text-slate-700">{op.responsible_name || "—"}</span></span>
            </div>
          </div>
          <div className="flex items-center gap-2 pl-7 lg:pl-0">
            <span className="text-sm text-muted-foreground">Stato attuale:</span>
            <OperationStatusBadge status={op.status} />
          </div>
        </div>
        <div className="mt-5">
          <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2">Avanzamento</div>
          <PhaseStepper status={op.status} phases={pipelineFor(op.type)} />
        </div>
      </Card>

      <Dialog open={sendOpen} onOpenChange={setSendOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Invia scheda PDF al cliente</DialogTitle></DialogHeader>
          <div className="py-2 space-y-3">
            <p className="text-sm text-muted-foreground">La scheda PDF completa dell'operazione verrà inviata come allegato all'indirizzo indicato. L'invio non modifica lo stato dell'operazione.</p>
            <Field label="Email cliente">
              <Input type="email" value={sendEmail} onChange={(e) => setSendEmail(e.target.value)} placeholder="cliente@esempio.it" data-testid="send-pdf-email-input" />
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendOpen(false)}>Annulla</Button>
            <Button onClick={doSend} disabled={sending || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(sendEmail)} data-testid="confirm-send-pdf"><Send className="h-4 w-4 mr-2" />{sending ? "Invio..." : "Invia"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-6">
          <Card className="p-6">
            <h3 className="font-semibold text-slate-800 mb-1">Aggiorna stato operazione</h3>
            <p className="text-xs text-muted-foreground mb-3">Puoi saltare le fasi non applicabili: l'operazione resta la stessa riga e lo storico viene conservato con data e responsabile.</p>
            <div className="flex flex-col sm:flex-row gap-3 sm:items-end">
              <div className="flex-1">
                <label className="text-xs font-semibold text-slate-600 uppercase tracking-wide">Cambia stato</label>
                <Select value={newStatus} onValueChange={setNewStatus}>
                  <SelectTrigger className="mt-1.5" data-testid="operation-status-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {pipelineFor(op.type).map((s) => (
                      <SelectItem key={s} value={s}>{s}{OPERATION_SKIPPABLE.includes(s) ? " (facoltativo)" : ""}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button onClick={updateStatus} disabled={newStatus === op.status} data-testid="update-operation-status-btn">
                <CheckCircle2 className="h-4 w-4 mr-2" />Aggiorna Stato
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-2">Selezionando <span className="font-medium text-slate-700">"Incasso completato"</span> l'operazione passa automaticamente in <span className="font-medium text-slate-700">Operazioni Concluse</span> mantenendo dati, documenti, storico e valori economici.</p>
          </Card>

          {op.type === "Cessione compromesso" && (
            <Card className="p-6" data-testid="operation-cession">
              <div className="flex items-center justify-between mb-1">
                <h3 className="font-semibold text-slate-800 flex items-center gap-2"><User className="h-4 w-4 text-primary" />Cessionario individuato</h3>
                <Button size="sm" onClick={saveDetails} disabled={saving} data-testid="save-cession-btn"><Save className="h-4 w-4 mr-2" />{saving ? "Salvataggio..." : "Salva"}</Button>
              </div>
              <p className="text-xs text-muted-foreground mb-4">Dati della fase "Cessionario individuato". Il prezzo concordato confluisce nel riepilogo economico per il calcolo di costi, margine e ROI.</p>
              <div className="grid sm:grid-cols-2 gap-4">
                <Field label="Cessionario"><Input value={form.cessionario} onChange={(e) => setForm({ ...form, cessionario: e.target.value })} data-testid="cession-cessionario" /></Field>
                <Field label="Contatto"><Input value={form.cession_contact} onChange={(e) => setForm({ ...form, cession_contact: e.target.value })} data-testid="cession-contact" /></Field>
                <MoneyInput label="Prezzo richiesto (€)" value={form.expected_sale_price} onChange={(v) => setForm({ ...form, expected_sale_price: v })} testid="cession-asking-price" />
                <MoneyInput label="Prezzo concordato (€)" value={form.actual_sale_price} onChange={(v) => setForm({ ...form, actual_sale_price: v })} testid="cession-agreed-price" />
                <Field label="Data prevista"><Input type="date" value={form.expected_sale_date || ""} onChange={(e) => setForm({ ...form, expected_sale_date: e.target.value })} data-testid="cession-expected-date" /></Field>
                <Field label="Note" className="sm:col-span-2"><Textarea value={form.cession_notes} onChange={(e) => setForm({ ...form, cession_notes: e.target.value })} data-testid="cession-notes" /></Field>
              </div>
            </Card>
          )}

          {/* Riepilogo economico completo */}
          <Card className="p-6" data-testid="operation-economics">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-slate-800">Riepilogo economico completo</h3>
              <Button size="sm" onClick={saveDetails} disabled={saving} data-testid="save-operation-btn"><Save className="h-4 w-4 mr-2" />{saving ? "Salvataggio..." : "Salva"}</Button>
            </div>

            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mb-2">Costi</div>
            <div className="grid sm:grid-cols-2 gap-4">
              <MoneyInput label="Prezzo di acquisto (€)" value={form.purchase_price} onChange={(v) => setForm({ ...form, purchase_price: v })} testid="econ-purchase-price" />
              <MoneyInput label="Imposte, notaio e commissioni (€)" value={form.taxes_notary_fees} onChange={(v) => setForm({ ...form, taxes_notary_fees: v })} testid="econ-taxes-notary" />
              <MoneyInput label="Costi di ristrutturazione (€)" value={form.renovation_costs} onChange={(v) => setForm({ ...form, renovation_costs: v })} testid="econ-renovation" />
              <MoneyInput label="Costi tecnici e professionali (€)" value={form.technical_costs} onChange={(v) => setForm({ ...form, technical_costs: v })} testid="econ-technical" />
              <MoneyInput label="Costi finanziari (€)" value={form.financial_costs} onChange={(v) => setForm({ ...form, financial_costs: v })} testid="econ-financial" />
              <MoneyInput label="Costi di mantenimento (€)" value={form.holding_costs} onChange={(v) => setForm({ ...form, holding_costs: v })} testid="econ-holding" />
              <MoneyInput label="Commercializzazione e vendita (€)" value={form.marketing_costs} onChange={(v) => setForm({ ...form, marketing_costs: v })} testid="econ-marketing" />
              <MoneyInput label="Imprevisti e altre spese (€)" value={form.contingency_costs} onChange={(v) => setForm({ ...form, contingency_costs: v })} testid="econ-contingency" />
            </div>
            <div className="mt-3 flex items-center justify-between rounded-lg bg-muted/50 px-3 py-2">
              <span className="text-sm text-muted-foreground">Costo totale dell'operazione</span>
              <span className="font-mono font-semibold text-slate-800" data-testid="econ-total-cost">{derived.pp > 0 || derived.total_cost > 0 ? eur(derived.total_cost) : TODO}</span>
            </div>

            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mt-6 mb-2">Vendita e incasso</div>
            <div className="grid sm:grid-cols-2 gap-4">
              <MoneyInput label="Prezzo di vendita previsto (€)" value={form.expected_sale_price} onChange={(v) => setForm({ ...form, expected_sale_price: v })} testid="econ-expected-sale" />
              <MoneyInput label="Prezzo di vendita effettivo (€)" value={form.actual_sale_price} onChange={(v) => setForm({ ...form, actual_sale_price: v })} testid="econ-actual-sale" />
              <Field label="Data prevista di vendita"><Input type="date" value={form.expected_sale_date || ""} onChange={(e) => setForm({ ...form, expected_sale_date: e.target.value })} data-testid="econ-expected-sale-date" /></Field>
              <Field label="Data effettiva di vendita"><Input type="date" value={form.actual_sale_date || ""} onChange={(e) => setForm({ ...form, actual_sale_date: e.target.value })} data-testid="econ-actual-sale-date" /></Field>
              <MoneyInput label="Spese di vendita (€)" value={form.selling_costs} onChange={(v) => setForm({ ...form, selling_costs: v })} testid="econ-selling-costs" />
              <MoneyInput label="Capitale investito (€)" value={form.invested_capital} onChange={(v) => setForm({ ...form, invested_capital: v })} testid="econ-invested-capital" />
              <MoneyInput label="Incasso ricevuto (€)" value={form.amount_received} onChange={(v) => setForm({ ...form, amount_received: v })} testid="econ-amount-received" />
              <Field label="Incasso ancora da ricevere (€)"><div className="h-10 flex items-center rounded-md border border-input bg-muted/40 px-3 font-mono text-sm text-slate-700" data-testid="econ-amount-pending">{money(derived.amount_pending)}</div></Field>
            </div>

            <div className="text-xs font-semibold text-slate-500 uppercase tracking-wide mt-6 mb-2">Risultato</div>
            <dl className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
              <div className="flex justify-between border-b border-border/60 pb-1.5"><dt className="text-muted-foreground">Utile/margine previsto</dt><dd className="font-mono" data-testid="econ-expected-profit">{money(derived.expected_profit)}</dd></div>
              <div className="flex justify-between border-b border-border/60 pb-1.5"><dt className="text-muted-foreground">Utile/margine effettivo</dt><dd className={`font-mono font-semibold ${derived.actual_profit === null ? "" : derived.actual_profit >= 0 ? "text-emerald-600" : "text-rose-600"}`} data-testid="econ-actual-profit">{money(derived.actual_profit)}</dd></div>
              <div className="flex justify-between border-b border-border/60 pb-1.5"><dt className="text-muted-foreground">Margine %</dt><dd className="font-mono" data-testid="econ-margin-pct">{pctFmt(derived.margin_pct)}</dd></div>
              <div className="flex justify-between border-b border-border/60 pb-1.5"><dt className="text-muted-foreground">ROI %</dt><dd className="font-mono" data-testid="econ-roi-pct">{pctFmt(derived.roi_pct)}</dd></div>
            </dl>
            <p className="text-xs text-muted-foreground mt-3">I valori calcolati si aggiornano automaticamente. Se un dato non è ancora inserito viene mostrato "{TODO}".</p>
          </Card>

          <Card className="p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-slate-800">Pratica e date</h3>
              <Button size="sm" variant="outline" onClick={saveDetails} disabled={saving} data-testid="save-operation-dates-btn"><Save className="h-4 w-4 mr-2" />{saving ? "Salvataggio..." : "Salva"}</Button>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <Field label="Data avvio"><Input type="date" value={form.start_date || ""} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></Field>
              <Field label="Data preliminare"><Input type="date" value={form.preliminary_date || ""} onChange={(e) => setForm({ ...form, preliminary_date: e.target.value })} /></Field>
              <Field label="Data rogito"><Input type="date" value={form.deed_date || ""} onChange={(e) => setForm({ ...form, deed_date: e.target.value })} /></Field>
              <Field label="Data incasso"><Input type="date" value={form.cash_in_date || ""} onChange={(e) => setForm({ ...form, cash_in_date: e.target.value })} /></Field>
              <Field label="Prossima azione" className="sm:col-span-2"><Input value={form.next_action} onChange={(e) => setForm({ ...form, next_action: e.target.value })} data-testid="operation-next-action" /></Field>
              <Field label="Note" className="sm:col-span-2"><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="operation-notes" /></Field>
            </div>
          </Card>

          <Card className="p-6">
            <h3 className="font-semibold text-slate-800 mb-3">Documenti</h3>
            <div className="space-y-2">
              {form.documents.length === 0 && <div className="text-sm text-muted-foreground">Nessun documento.</div>}
              {form.documents.map((d) => (
                <label key={d.name} className="flex items-center gap-2 text-sm cursor-pointer" data-testid={`operation-doc-${d.name}`}>
                  <Checkbox checked={!!d.done} onCheckedChange={() => toggleDoc(d.name)} />
                  <span className={d.done ? "line-through text-muted-foreground" : "text-slate-700"}>{d.name}</span>
                </label>
              ))}
            </div>
          </Card>
        </div>

        <div className="space-y-6">
          <Card className="p-6">
            <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2"><CalendarClock className="h-4 w-4 text-primary" />Promemoria</h3>
            <div className="space-y-2" data-testid="operation-reminders">
              {(op.reminders || []).length === 0 && <div className="text-sm text-muted-foreground">Nessun promemoria. Imposta la data di rogito o incasso per generarlo automaticamente.</div>}
              {(op.reminders || []).map((r) => (
                <div key={r.id} className={`rounded-lg border px-3 py-2 ${r.status === "Chiuso" ? "border-border bg-muted/40" : "border-amber-100 bg-amber-50/60"}`} data-testid={`operation-reminder-${r.id}`}>
                  <div className="flex items-center justify-between gap-2">
                    <div className="text-sm font-medium text-slate-800">{r.next_action}</div>
                    <span className={`text-xs font-semibold ${r.status === "Chiuso" ? "text-emerald-600" : "text-amber-700"}`}>{r.status === "Chiuso" ? "Completato" : dateIt(r.next_action_date)}</span>
                  </div>
                  {r.notes && <div className="text-xs text-muted-foreground mt-0.5">{r.notes}</div>}
                  {r.status !== "Chiuso" && (
                    <Button size="sm" variant="outline" className="mt-2 h-7" onClick={() => completeReminder(r)} data-testid={`complete-reminder-${r.id}`}>
                      <CheckCircle2 className="h-3.5 w-3.5 mr-1" />Segna completato
                    </Button>
                  )}
                </div>
              ))}
            </div>
          </Card>

          <Card className="p-6">
            <h3 className="font-semibold text-slate-800 mb-4">Sintesi economica</h3>
            <dl className="space-y-2 text-sm">
              <div className="flex justify-between"><dt className="text-muted-foreground">Prezzo acquisto</dt><dd className="font-mono font-medium">{derived.pp > 0 ? eur(derived.pp) : TODO}</dd></div>
              <div className="flex justify-between"><dt className="text-muted-foreground">Costo totale</dt><dd className="font-mono">{derived.pp > 0 || derived.total_cost > 0 ? eur(derived.total_cost) : TODO}</dd></div>
              <div className="flex justify-between"><dt className="text-muted-foreground">Prezzo vendita eff.</dt><dd className="font-mono">{num(form.actual_sale_price) > 0 ? eur(num(form.actual_sale_price)) : TODO}</dd></div>
              <div className="flex justify-between"><dt className="text-muted-foreground">Utile effettivo</dt><dd className={`font-mono font-semibold ${derived.actual_profit === null ? "" : derived.actual_profit >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{money(derived.actual_profit)}</dd></div>
              <div className="flex justify-between"><dt className="text-muted-foreground">ROI</dt><dd className="font-mono">{pctFmt(derived.roi_pct)}</dd></div>
            </dl>
          </Card>

          {op.status === CONCLUDED && (
            <Card className="p-6">
              <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2"><Mail className="h-4 w-4 text-primary" />Storico invii scheda PDF</h3>
              <div className="space-y-2" data-testid="operation-pdf-sends">
                {(op.pdf_sends || []).length === 0 && <div className="text-sm text-muted-foreground">Nessun invio effettuato.</div>}
                {(op.pdf_sends || []).slice().reverse().map((s, i) => (
                  <div key={i} className="rounded-lg border border-border px-3 py-2" data-testid={`pdf-send-${i}`}>
                    <div className="text-sm font-medium text-slate-800 break-all">{s.email}</div>
                    <div className="text-xs text-muted-foreground">{dateTimeIt(s.at)}{s.by_name ? ` · ${s.by_name}` : ""}</div>
                  </div>
                ))}
              </div>
            </Card>
          )}

          <Card className="p-6">
            <h3 className="font-semibold text-slate-800 mb-4">Collegamenti</h3>
            <div className="space-y-2">
              {op.property_id && (
                <button className="w-full flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm hover:bg-muted/50 text-left" onClick={() => navigate(`/immobili/${op.property_id}`)} data-testid="op-link-property">
                  <Building2 className="h-4 w-4 text-primary" /><span className="font-medium">{op.property_label}</span>
                </button>
              )}
              <div className="flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm">
                <User className="h-4 w-4 text-primary" /><span>{op.contact_name || "Nessun contatto"}</span>
              </div>
              {op.proposal_id && (
                <button className="w-full flex items-center gap-2 rounded-lg border border-border px-3 py-2 text-sm hover:bg-muted/50 text-left" onClick={() => navigate("/proposte")} data-testid="op-link-proposal">
                  <History className="h-4 w-4 text-primary" /><span>Proposta di origine</span>
                </button>
              )}
            </div>
          </Card>

          <Card className="p-6">
            <h3 className="font-semibold text-slate-800 mb-4 flex items-center gap-2"><History className="h-4 w-4 text-primary" />Cronologia stati</h3>
            <ol className="space-y-3" data-testid="operation-history">
              {(op.status_history || []).slice().reverse().map((h, i) => (
                <li key={i} className="flex items-start gap-3">
                  <div className="mt-1 h-2 w-2 rounded-full bg-primary shrink-0" />
                  <div>
                    <div className="text-sm font-medium text-slate-800">{h.status}</div>
                    <div className="text-xs text-muted-foreground">{dateTimeIt(h.at) || dateIt(h.at)}{h.by_name ? ` · ${h.by_name}` : ""}</div>
                  </div>
                </li>
              ))}
            </ol>
          </Card>
        </div>
      </div>
    </div>
  );
}
